import React, { useState, useEffect } from 'react';
import GroupItem, { GroupProps } from '../GroupItem/GroupItem';
import GroupChatDetails from '../GroupChatDetails/GroupChatDetails';
import GroupDetails from '../GroupDetails/GroupDetails';
import './GroupsList.css'; // Importing the external CSS file
import StyledBgContainer from '../../../components/StyledBgContainer/StyledBgContainer';
import { Box, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useContext } from 'react';
import messageIcon from '../../../assets/Message 28.svg';
import NotificationIcon from '../../../assets/Notification 1.svg';
import LeftArrow from '../../../assets/Left 1.svg';
import { useWebSocket } from '../../../context/WebSocketContext';
import { Modal } from '@mui/material';
import NotificationwhiteIcon from '../../../assets/NotoficationIcon.svg';

import { getStage } from '../../../services/NetworkServices';
import { storeStageNumber } from '../../../utils/auth-token-utils/cookie';
import {
  ProfileContext,
  ProfileContextProps,
} from '../../../context/ProfileContext';
import {
  BabysitterContext,
  BabysitterContextProps,
} from '../../../context/BabysitterContext';

type Props = {};

const GroupsList: React.FC<Props> = () => {
  const [showChatList, setShowChatList] = useState(true);
  const [showChatDetails, setShowChatDetails] = useState(false);
  const [showGroupDetails, setShowGroupDetails] = useState(false);
  const [groupInfo, setGroupInfo] = useState<GroupProps | null>(null);
  const { userProfile } = useContext(ProfileContext) as ProfileContextProps;
  const [showNotificationListPopup, setShowNotificationListPopup] =
    useState<boolean>(false);

  const { socket } = useWebSocket();

  const {
    markAsRead,
    notificationsList,
    notificationsCount,
    handleNotificationsCount,
    handleNotificationsList,
  } = useContext(BabysitterContext) as BabysitterContextProps;

  const navigate = useNavigate();

  const formatNotification = (data: any): any => {
    return {
      createdAt: new Date().toISOString(),
      message: data.message,
      notificationType: data.notificationType || data.type, // Ensure correct key for type
      read: false,
      updatedAt: new Date().toISOString(),
      userFrom: data.userFrom || null,
      userTo: data.userTo || null,
      _id: data._id || generateUniqueId(), // Generate a unique ID if not provided
    };
  };

  // Utility function to generate a unique ID if none is provided
  const generateUniqueId = () => {
    return '_' + Math.random().toString(36).substr(2, 9);
  };

  const handleChatIconClick = () => {
    window.location.reload(); // This will refresh the entire page
  };

  useEffect(() => {
    const fetchStageDetails = async () => {
      try {
        const response = await getStage();
        const data = await response.data;
        if (data) {
          const stage = data.data.stage;
          storeStageNumber(stage);
          if (stage === '1') {
            navigate('/supervisor/createProfile');
          }
        }
      } catch (error) {
        console.error('Error fetching profile details:', error);
      }
    };
    fetchStageDetails();
  }, [navigate]);

  useEffect(() => {
    const handleNotification = (data: any) => {
      console.log('notification received', data);

      const todayDate = new Date().toISOString().split('T')[0]; // Get today's date in 'YYYY-MM-DD' format

      const newNotification = formatNotification(data); // Format the notification

      // Create a copy of the current list
      const updatedList = [...notificationsList]; // You might need to replace this with how you are managing the list

      // Find if today's date already exists in the list
      const existingDateIndex = updatedList.findIndex(
        (item) => item.date === todayDate,
      );

      if (existingDateIndex !== -1) {
        // If today's date exists, unshift the new notification to the start of the notifications array
        updatedList[existingDateIndex].notifications.unshift(newNotification);
      } else {
        // If today's date doesn't exist, create a new entry at the beginning
        updatedList.unshift({
          date: todayDate,
          notifications: [newNotification],
        });
      }

      // Pass the updated list to handleNotificationsList
      handleNotificationsList(updatedList);
    };

    socket?.on('group-message-notification', handleNotification);
    socket?.on('face-auth-success-notification', handleNotification);
    socket?.on('face-auth-fail-notification', handleNotification);
    socket?.on('broadcast-notification', handleNotification);

    // Cleanup socket listeners on unmount
    return () => {
      socket?.off('group-message-notification', handleNotification);
      socket?.off('face-auth-success-notification', handleNotification);
      socket?.off('face-auth-fail-notification', handleNotification);
      socket?.off('broadcast-notification', handleNotification);
    };
  }, [socket, notificationsList]);

  const handleNotificationList = async () => {
    setShowNotificationListPopup(true);
  };

  return (
    <StyledBgContainer>
      <div className="group-chat-list-container">
        <div className="group-chat-list-content">
          {showChatList && (
            <>
              <Box
                gap={2}
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' }, // Column on smaller screens, row on larger
                  justifyContent: 'space-between',
                  alignItems: { xs: 'start', sm: 'center' },
                  px: { xs: 2, sm: 10 },
                  py: 4,
                  backgroundImage: `url(/path/to/background-image.jpg)`, // Add background image as seen in the uploaded image
                  backgroundSize: 'cover', // Ensure background image covers the area
                }}
              >
                {/* Left Section: Back Icon + Group Chat */}
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <img
                    src={LeftArrow}
                    className="group-chat-list-back-icon"
                    onClick={() => navigate('/supervisor/dashboard')}
                    style={{ width: '24px', height: '24px', cursor: 'pointer' }} // Ensure back arrow is clickable
                  />
                  <Typography
                    variant="body1"
                    sx={{
                      color: '#fff',
                      marginLeft: '10px', // Add space between arrow and text
                      fontWeight: 'bold',
                      fontSize: { xs: '16px', sm: '18px' }, // Adjust text size for responsiveness
                    }}
                  >
                    Group Chat
                  </Typography>
                </Box>

                {/* Right Section: Notification, Message, Profile */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px', // Space between icons
                  }}
                >
                  {/* Notification Icon with Counter */}
                  <Box
                    sx={{
                      backgroundColor: '#FB5012',
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      position: 'relative',
                      cursor: 'pointer',
                    }}
                    onClick={handleNotificationList}
                  >
                    <img
                      src={NotificationIcon}
                      alt="Notification Icon"
                      style={{ width: '20px' }}
                    />
                    {notificationsCount > 0 && (
                      <Typography
                        sx={{
                          position: 'absolute',
                          top: -10,
                          right: -10,
                          backgroundColor: 'red',
                          borderRadius: '50%',
                          padding: '2px 6px',
                          fontSize: '12px',
                          color: '#fff',
                        }}
                      >
                        {notificationsCount}
                      </Typography>
                    )}
                  </Box>

                  {/* Chat Icon */}
                  <Box
                    sx={{
                      backgroundColor: '#FB5012',
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      cursor: 'pointer',
                    }}
                    onClick={handleChatIconClick}
                  >
                    <img
                      src={messageIcon}
                      alt="Chat Icon"
                      style={{ width: '20px' }}
                    />
                  </Box>

                  {/* Profile Picture */}
                  <img
                    src={
                      userProfile.profilePic
                        ? userProfile.profilePic
                        : 'https://www.pngitem.com/pimgs/m/146-1468479_my-profile-icon-blank-profile-picture-circle-hd.png'
                    }
                    className="header-profile-pic"
                    alt="Profile"
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      cursor: 'pointer',
                    }}
                    onClick={() => navigate('/supervisor/edit-profile')}
                  />
                </Box>
              </Box>

              {/* Group Item Section */}
              <Box sx={{ mx: { xs: 2, sm: 10 } }}>
                <GroupItem
                  onClick={(groupInfo: GroupProps) => {
                    setGroupInfo(groupInfo);
                    setShowChatList(!showChatList);
                    setShowChatDetails(true);
                  }}
                />
              </Box>

              {/* Notification Modal */}
              {notificationsList.length >= 0 && (
                <Modal
                  open={showNotificationListPopup}
                  onClose={() => setShowNotificationListPopup(false)}
                  sx={{
                    position: 'absolute',
                    top: { xs: '115px', md: '115px' }, // Same top positioning for both xs and md
                    left: { xs: '10px', md: 'auto', lg: '600px' }, // Apply left padding for xs to avoid fixed space
                    right: { xs: '10px', md: '40px', lg: '70px' }, // Ensure modal doesn't overflow on xs
                    transform: { xs: 'none', md: 'none' }, // No transformation needed
                    width: { xs: 'calc(100% - 20px)', md: 'auto' }, // Full width minus padding for xs, auto for md
                    maxWidth: { md: 'auto' }, // Prevent max width overflow on larger screens
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '10px',
                      backdropFilter: 'blur(21px)', // Blur effect
                      background: '#FFFFFF1A',
                      borderTop: '1px solid #365F91',
                      borderLeft: '1px solid #365F91',
                      borderRight: '1px solid #365F91', // Transparent border for gradient effect
                      borderRadius: '24px', // Rounded corners
                      padding: '20px', // Padding inside the box
                      outline: 'none', // No outline on focus
                      width: '100%', // Full width inside the box
                    }}
                  >
                    {notificationsList.length > 0 ? (
                      <>
                        {notificationsList
                          .flatMap((notification) => notification.notifications) // Flatten the array
                          .slice(0, 4) // Limit to the first 4 notifications
                          .map((eachNotification: any, index) => (
                            <Box
                              key={index}
                              sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                color: 'white',
                                gap: '10px',
                              }}
                            >
                              <img
                                src={NotificationwhiteIcon}
                                alt="Notification Icon"
                                style={{ width: '30px', height: '30px' }}
                              />
                              <p>{eachNotification?.message}</p>
                            </Box>
                          ))}
                        <a
                          href="/supervisor/notifications"
                          style={{ color: 'white' }}
                        >
                          View All
                        </a>
                      </>
                    ) : (
                      <div style={{ color: 'white', textAlign: 'center' }}>
                        No data found
                      </div>
                    )}
                  </Box>
                </Modal>
              )}
            </>
          )}
        </div>

        {showChatDetails && (
          <GroupChatDetails
            groupInfo={groupInfo}
            onClick={() => {
              setShowChatList(!showChatList);
              setShowChatDetails(true);
            }}
            goBack={() => {
              setShowChatDetails(false);
              setShowChatList(true);
            }}
            onClickDetails={() => {
              setShowChatDetails(false);
              setShowGroupDetails(true);
            }}
          />
        )}

        {showGroupDetails && (
          <GroupDetails
            groupInfo={groupInfo}
            goBack={() => {
              setShowGroupDetails(false);
              setShowChatDetails(true);
            }}
          />
        )}
      </div>
    </StyledBgContainer>
  );
};

export default GroupsList;
